import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatIST } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Eye, RefreshCw, LayoutGrid, List, Search, ChevronDown, ChevronRight, User, Check,
} from 'lucide-react';
import KanbanBoard from '@/components/KanbanBoard';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/useIsMobile';
import PhoneDesignQueue from './Design/PhoneDesignQueue';

import { Screen, ScreenSplit, Pane, CommandBar, StatStrip, DataGrid, CellButton } from '@/components/data';
import { PRIORITY_MAP } from '@/lib/status-colors';
import { canAssignDesigners } from '@/lib/constants';

/**
 * The design queue and the people who can take it, side by side.
 *
 * Assigning work used to mean reading the Designer Workload card at the bottom
 * of the page, scrolling back up, opening a dialog, and picking from a select
 * that repeated the same workload numbers you had just scrolled away from. Four
 * clicks and two context switches to answer "who is free".
 *
 * The two facts you need — what is waiting, and who is loaded — now sit beside
 * each other, and the Designer column is the control: click the cell, pick a
 * name, done.
 */

const DESIGN_STATUS_MAP = {
  pending_assignment: { label: 'Pending', class: 'bg-slate-100 text-slate-600' },
  assigned: { label: 'Assigned', class: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'In Progress', class: 'bg-amber-100 text-amber-700' },
  completed: { label: 'Done', class: 'bg-green-100 text-green-700' },
  not_required: { label: 'N/A', class: 'bg-gray-100 text-gray-500' },
};

const PRIORITY_TEXT = { high: 'text-red-600', medium: 'text-amber-600', low: 'text-emerald-600' };

const isItemOpen = (item) =>
  item.design_status !== 'completed' && item.design_status !== 'not_required';

const orderDesigner = (order) =>
  order.computed_designer_name || order.assigned_designer_name || null;

/**
 * The designer cell: shows who has it, and opens the picker when clicked.
 *
 * Lives outside the page component so the memoised column set cannot capture a
 * stale copy of it — the bug that makes an inline sub-component quietly show
 * yesterday's workload numbers.
 */
function DesignerPicker({ order, item, designers, busyId, currentUserId, canAssign, onAssign }) {
  const [open, setOpen] = useState(false);
  const current = item ? item.assigned_designer_name : orderDesigner(order);
  const currentId = item ? item.assigned_designer_id : order.assigned_designer_id;
  const multiple = !item && order.has_multiple_designers;
  const key = item ? item.id : order.id;

  // a designer reads this column; they claim from My Designs rather than
  // hand work to each other, so there is nothing here for them to open
  if (!canAssign) {
    return current
      ? <span className="truncate px-1 text-[12px] text-slate-700">{current}</span>
      : <span className="px-1 text-[12px] text-amber-600">Unassigned</span>;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          disabled={busyId === key}
          data-testid={item ? `assign-item-${item.id}` : `assign-btn-${order.order_id}`}
          className="flex w-full items-center gap-1 rounded px-1 py-0.5 text-left text-[12px] transition-colors hover:bg-slate-200/60 disabled:opacity-50"
        >
          {current ? (
            <>
              <span className="truncate text-slate-700">{current}</span>
              {multiple && <Badge variant="outline" className="shrink-0 px-1 py-0 text-[9px]">+</Badge>}
            </>
          ) : (
            <span className="text-amber-600">Unassigned</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-1" onClick={(e) => e.stopPropagation()}>
        <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
          {item ? 'Assign this item' : `Assign all ${order.line_items?.length || 0} items`}
        </p>
        {designers.map(d => (
          <button
            key={d.id}
            type="button"
            onClick={() => { setOpen(false); onAssign(order, item, d.id); }}
            className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-slate-100"
          >
            <span className="flex min-w-0 items-center gap-1.5">
              {d.id === currentId && <Check className="h-3 w-3 shrink-0 text-brand-blue" />}
              <span className="truncate">{d.full_name}</span>
            </span>
            <span className="shrink-0 text-[10px] text-slate-400">
              {d.current_workload}o · {d.item_workload || 0}i
            </span>
          </button>
        ))}
        {currentUserId && (
          <button
            type="button"
            onClick={() => { setOpen(false); onAssign(order, item, currentUserId); }}
            className="mt-0.5 flex w-full items-center gap-1.5 border-t border-slate-100 px-2 py-1.5 text-left text-xs font-medium text-brand-blue hover:bg-blue-50"
          >
            <User className="h-3 w-3" /> Assign to me
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default function DesignQueuePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ unassigned: 0, in_progress: 0 });
  const [designers, setDesigners] = useState([]);
  const [filter, setFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  const [busyId, setBusyId] = useState(null);
  const canAssign = canAssignDesigners(user);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.status = filter;
      const [queueRes, designersRes] = await Promise.all([
        api.get('/design/queue', { params }),
        canAssign ? api.get('/design/designers') : Promise.resolve({ data: [] }),
      ]);
      setOrders(queueRes.data.orders);
      setStats(queueRes.data.stats);
      setDesigners(designersRes.data);
    } catch { toast.error('Failed to load design queue'); }
    setLoading(false);
  }, [filter, canAssign]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleExpanded = useCallback((orderId) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId); else next.add(orderId);
      return next;
    });
  }, []);

  /**
   * One call for every case. Assigning and reassigning were four handlers and
   * two dialogs; the endpoints differ only by verb and by whether a line item is
   * named, which the caller already knows.
   */
  const assign = useCallback(async (order, item, designerId) => {
    const already = item ? item.assigned_designer_id : order.assigned_designer_id;
    const verb = already ? 'reassign' : 'assign';
    const url = item
      ? `/design/${order.id}/line-items/${item.id}/${verb}`
      : `/design/${order.id}/${verb}`;
    const key = item ? item.id : order.id;
    setBusyId(key);
    try {
      await api.put(url, { designer_id: designerId });
      const who = designers.find(d => d.id === designerId)?.full_name || 'designer';
      toast.success(`${item ? 'Item' : 'Order'} ${already ? 'reassigned' : 'assigned'} to ${who}`);
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to assign');
    }
    setBusyId(null);
  }, [designers, fetchData]);

  const filteredOrders = useMemo(() => {
    if (!searchQuery) return orders;
    const q = searchQuery.toLowerCase();
    return orders.filter(o =>
      o.order_id?.toLowerCase().includes(q) ||
      o.voice_number?.toLowerCase().includes(q) ||
      o.customer_name?.toLowerCase().includes(q) ||
      o.firm_name?.toLowerCase().includes(q));
  }, [orders, searchQuery]);

  // Orders, with their line items folded in underneath when opened. One flat
  // list so the item columns line up with the order columns above them.
  const rows = useMemo(() => {
    const out = [];
    for (const order of filteredOrders) {
      out.push({ ...order, __kind: 'order', __rowId: order.id });
      if (expanded.has(order.id)) {
        for (const item of order.line_items || []) {
          out.push({
            ...item, __kind: 'item', __depth: 1, __order: order,
            __rowId: `${order.id}:${item.id}`,
          });
        }
      }
    }
    return out;
  }, [filteredOrders, expanded]);

  const columns = useMemo(() => ([
    {
      key: 'name',
      header: 'Item / Customer',
      width: 'minmax(14rem,1.5fr)',
      render: (row) => {
        if (row.__kind === 'item') {
          const status = DESIGN_STATUS_MAP[row.design_status] || DESIGN_STATUS_MAP.pending_assignment;
          return (
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate text-slate-700">
                {row.item_name || row.product_subtype || row.order_type}
              </span>
              <Badge className={`shrink-0 text-[9px] ${status.class}`}>{status.label}</Badge>
            </span>
          );
        }
        const items = row.line_items || [];
        const done = items.filter(i => !isItemOpen(i)).length;
        return (
          <span className="flex min-w-0 items-center gap-1.5">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); toggleExpanded(row.id); }}
              aria-label={expanded.has(row.id) ? 'Collapse items' : 'Expand items'}
              data-testid={`expand-order-${row.order_id}`}
              className="shrink-0 rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
            >
              {expanded.has(row.id) ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </button>
            <span className="min-w-0">
              <span className="block truncate font-semibold text-slate-900">
                {items[0]?.item_name || row.customer_name}
              </span>
              <span className="block truncate text-[11px] leading-tight text-slate-500">
                {row.customer_name}{row.firm_name ? ` — ${row.firm_name}` : ''}
              </span>
            </span>
            {items.length > 0 && (
              <span className="shrink-0 text-[10px] text-slate-400">{done}/{items.length}</span>
            )}
          </span>
        );
      },
    },
    {
      key: 'order_id',
      header: 'Order',
      width: '10rem',
      render: (row) => row.__kind === 'item'
        ? <span className="text-[11px] text-slate-400">Qty {row.quantity?.toLocaleString()}</span>
        : <span className="block truncate text-[11px] text-slate-400" title={row.voice_number}>{row.order_id}</span>,
    },
    {
      key: 'priority',
      header: 'Priority',
      width: '5.5rem',
      render: (row) => row.__kind === 'item' ? null : (
        <span className={`text-[11px] font-medium ${PRIORITY_TEXT[row.priority] || 'text-slate-500'}`}>
          {PRIORITY_MAP[row.priority]?.label || row.priority}
        </span>
      ),
    },
    {
      key: 'designer',
      header: 'Designer',
      width: '11rem',
      render: (row) => row.__kind === 'item'
        ? (isItemOpen(row)
            ? <DesignerPicker order={row.__order} item={row} designers={designers}
                busyId={busyId} currentUserId={user?.id} canAssign={canAssign} onAssign={assign} />
            : <span className="px-1 text-[11px] text-slate-400">—</span>)
        : <DesignerPicker order={row} designers={designers}
            busyId={busyId} currentUserId={user?.id} canAssign={canAssign} onAssign={assign} />,
    },
    {
      key: 'expected_delivery_date',
      header: 'Delivery',
      width: '7rem',
      render: (row) => row.__kind === 'item' ? null : (
        <span className="whitespace-nowrap text-[11px] text-slate-600">
          {row.expected_delivery_date ? formatIST(row.expected_delivery_date, { dateOnly: true }) : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '3rem',
      align: 'right',
      render: (row) => row.__kind === 'item' ? null : (
        <CellButton
          icon={Eye}
          label="Open order"
          iconOnly
          onClick={() => navigate(`/orders/${row.id}`)}
          testId={`view-order-${row.order_id}`}
        />
      ),
    },
  ]), [expanded, toggleExpanded, designers, busyId, user?.id, canAssign, assign, navigate]);

  // the same queue as cards, the designer line still the control; the board
  // view is a desktop's — four columns have no room on a phone
  if (isMobile) {
    return (
      <PhoneDesignQueue
        orders={filteredOrders}
        stats={stats}
        totalInQueue={orders.length}
        designers={designers}
        filter={filter}
        setFilter={setFilter}
        loading={loading}
        setSearchQuery={setSearchQuery}
        expanded={expanded}
        toggleExpanded={toggleExpanded}
        busyId={busyId}
        canAssign={canAssign}
        currentUserId={user?.id}
        onAssign={assign}
        onRefresh={fetchData}
        onOpenOrder={(order) => navigate(`/orders/${order.id}`)}
      />
    );
  }

  const statList = [
    { key: null, label: 'in queue', value: orders.length, tone: 'blue' },
    { key: 'unassigned', label: 'Unassigned', value: stats.unassigned, tone: stats.unassigned ? 'warn' : 'default' },
    { key: 'assigned', label: 'Assigned', value: stats.in_progress, tone: 'default' },
  ];

  return (
    <Screen data-testid="design-queue-page">
      <CommandBar
        title="Design Queue"
        actions={[
          { key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: fetchData },
          {
            key: 'view',
            label: viewMode === 'list' ? 'Kanban' : 'List',
            icon: viewMode === 'list' ? LayoutGrid : List,
            onClick: () => setViewMode(viewMode === 'list' ? 'kanban' : 'list'),
            testId: viewMode === 'list' ? 'kanban-view-btn' : 'list-view-btn',
          },
        ]}
      >
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            className="h-7 pl-8 text-xs"
            placeholder="Order ID, voice number, customer…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </CommandBar>

      <StatStrip
        stats={statList}
        activeKey={filter}
        onSelect={setFilter}
        data-testid="design-stats"
      />

      <ScreenSplit
        railWidth="17rem"
        rail={
          <Pane title="Designers" aside={`${designers.length} active`} className="max-h-full">
            {designers.length === 0 ? (
              <p className="p-4 text-center text-xs text-slate-400">No designers found</p>
            ) : designers.map(d => {
              const load = d.current_workload || 0;
              return (
                <div
                  key={d.id}
                  className="flex items-center justify-between gap-2 border-b border-slate-100 px-2.5 py-1.5 last:border-b-0"
                  data-testid={`designer-card-${d.id}`}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-[13px] font-medium text-slate-700">{d.full_name}</span>
                    <span className="block text-[10px] capitalize text-slate-400">{d.role?.replace('_', ' ')}</span>
                  </span>
                  <span className="flex shrink-0 items-center gap-1 text-[11px] tabular-nums">
                    <span className={load > 3 ? 'font-semibold text-red-600' : 'text-slate-600'}>{load}</span>
                    <span className="text-slate-400">orders</span>
                    {d.item_workload > 0 && (
                      <span className="text-slate-400">· {d.item_workload}i</span>
                    )}
                  </span>
                </div>
              );
            })}
          </Pane>
        }
      >
        {viewMode === 'kanban' ? (
          <Pane title="Board" bodyClassName="p-3">
            <KanbanBoard
              columns={[
                { id: 'pending_assignment', title: 'Pending Assignment', color: 'slate',
                  orders: filteredOrders.filter(o => !o.assigned_designer_id && !o.line_items?.some(i => i.assigned_designer_id)) },
                { id: 'assigned', title: 'Assigned', color: 'blue',
                  orders: filteredOrders.filter(o => (o.assigned_designer_id || o.line_items?.some(i => i.assigned_designer_id)) && o.design_status !== 'completed') },
                { id: 'in_progress', title: 'In Progress', color: 'amber',
                  orders: filteredOrders.filter(o => o.design_status === 'in_progress') },
                { id: 'completed', title: 'Completed', color: 'green',
                  orders: filteredOrders.filter(o => o.design_status === 'completed') },
              ]}
              onColumnChange={() => toast.info('Assign from the list view — the Designer column is the control')}
            />
          </Pane>
        ) : (
          <Pane
            title="Queue"
            aside={`${filteredOrders.length} order${filteredOrders.length === 1 ? '' : 's'}`}
            scroll={false}
            bodyClassName="flex flex-col"
            actions={
              <button
                type="button"
                onClick={() => setExpanded(expanded.size ? new Set() : new Set(filteredOrders.map(o => o.id)))}
                className="rounded px-1.5 py-0.5 text-[10px] font-normal normal-case tracking-normal text-blue-100 hover:bg-white/20"
              >
                {expanded.size ? 'Collapse all' : 'Expand all'}
              </button>
            }
          >
            <DataGrid
              data-testid="design-queue-table"
              columns={columns}
              rows={rows}
              loading={loading}
              getRowId={(row) => row.__rowId}
              empty="No orders in design queue"
            />
          </Pane>
        )}
      </ScreenSplit>
    </Screen>
  );
}
